import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Box, Button, CircularProgress, Alert, Tab, Tabs, Chip, Snackbar, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DefaultLayout from '../layouts/DefaultLayout';
import GlobalHeader from '../components/GlobalHeader';
import { queryPackingOperationDetail, queryItemTypesWithLocations, queryTripDetail, PackingOperation, type ItemType } from '../schema';
import OperationHeader from '../components/OperationHeader';
import TabPanel from '../components/TabPanel';
import PackingTasksList from '../components/PackingTasksList';
import CurrentInventoryPanel from '../components/CurrentInventoryPanel';
import FinalConfirmationTab from '../components/FinalConfirmationTab';
import CompleteOperationDialog from '../components/CompleteOperationDialog';
import SupplyFlowSection from '../components/SupplyFlowSection';
import QuantitySummary from '../components/QuantitySummary';
import { buildOperationSupplyFlowData, edgeToTaskLocationIds, operationNodeToLogicalId } from '../utils/operationSupplyFlow';
import { buildTripTrailerSupplyFlowContext } from '../utils/tripTrailerSupplyFlowContext';
import type { TripForIssuesCount } from '../utils/tripIssuesCount';
import { quantityPlansFromOperationFlows } from '../utils/quantityCalculation';
import { computeInsufficientItemsFromPlans } from '../utils/inventoryCheckLogic';
import type { ItemLocationForLookup } from '../utils/taskCompletion';
import { filterValidItemLocations } from '../utils/instantDbGuards';
import { db } from '../instant';
import { id } from '@instantdb/react';

export default function OperationDetailScreen() {
    const { operationId } = useParams<{ operationId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const activeTab = pathname.endsWith('/inventory') ? 3 : pathname.endsWith('/confirmation') ? 1 : pathname.endsWith('/supply') ? 2 : 0; // tasks (path ends with /tasks) or default
    const [inventoryPanelOpen, setInventoryPanelOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const { data: operationData, isLoading, error } = queryPackingOperationDetail(operationId ?? null);
    const { data: itemTypesData } = queryItemTypesWithLocations();

    const operation = useMemo(() => operationData?.packingOperations?.[0] ?? null, [operationData]);

    const linkedTripId = useMemo(() => {
        const t = (operation as { trip?: { id?: string } | null } | null)?.trip;
        return t?.id ?? null;
    }, [operation]);

    const { data: linkedTripDetailData } = queryTripDetail(linkedTripId);

    const tasks = useMemo(() => {
        if (!operation) return [];
        return (operation.packingTasks || []) as PackingOperation['packingTasks'][number][];
    }, [operation]);

    /** DAG + flows from operation tasks for supply flow section */
    const supplyFlowData = useMemo(() => (operation ? buildOperationSupplyFlowData(operation) : null), [operation]);

    /** Linked trip: route + trailers-per-edge for trailer–cargo checks on supply flow (warn only). */
    const tripTrailerCtx = useMemo(
        () =>
            buildTripTrailerSupplyFlowContext(
                linkedTripDetailData?.trips?.[0] as TripForIssuesCount | undefined,
                linkedTripDetailData?.trailerCargoIncompatibilities
            ),
        [linkedTripDetailData?.trips, linkedTripDetailData?.trailerCargoIncompatibilities]
    );

    const resolveTrailerBanIndices = useCallback(
        (fromIdx: number, toIdx: number) => {
            if (!supplyFlowData || !tripTrailerCtx) return null;
            const fromNode = supplyFlowData.dagNodes[fromIdx];
            const toNode = supplyFlowData.dagNodes[toIdx];
            if (!fromNode || !toNode) return null;
            const fromId = operationNodeToLogicalId(fromNode);
            const toId = operationNodeToLogicalId(toNode);
            const tf = tripTrailerCtx.logicalIdToTripIndex.get(fromId);
            const tt = tripTrailerCtx.logicalIdToTripIndex.get(toId);
            if (tf === undefined || tt === undefined) return null;
            return { from: tf, to: tt };
        },
        [supplyFlowData, tripTrailerCtx]
    );

    /** Source event and storage IDs for "available at source" item types */
    const sourceEventIds = useMemo(
        () => new Set((operation?.sourceEvents ?? []).map((e: { id: string }) => e.id)),
        [operation?.sourceEvents]
    );
    const sourceStorageLocationIds = useMemo(
        () => new Set((operation?.sourceStorageLocations ?? []).map((s: { id: string }) => s.id)),
        [operation?.sourceStorageLocations]
    );

    /**
     * Flat list of all item locations built from the reactive useQuery result.
     * Passed into task completion functions to avoid db.queryOnce (which
     * requires network and times out offline).
     */
    const allItemLocations = useMemo((): ItemLocationForLookup[] => {
        const result: ItemLocationForLookup[] = [];
        for (const itemType of (itemTypesData?.itemTypes ?? []) as Array<{
            id: string;
            itemLocations?: Array<{
                id: string;
                quantity?: number;
                event?: { id: string } | null;
                storageLocation?: { id: string } | null;
            }>;
        }>) {
            for (const loc of filterValidItemLocations(itemType.itemLocations ?? [])) {
                result.push({
                    id: loc.id,
                    quantity: loc.quantity,
                    itemTypeId: itemType.id,
                    event: loc.event ?? null,
                    storageLocation: loc.storageLocation ?? null,
                });
            }
        }
        return result;
    }, [itemTypesData?.itemTypes]);

    /** Item types for supply flow autocomplete: available at any operation source, or already have tasks in this operation. */
    const planItemTypes = useMemo((): ItemType[] => {
        const itemTypes = (itemTypesData?.itemTypes ?? []) as Array<
            ItemType & {
                itemLocations?: Array<{ event?: { id: string } | null; storageLocation?: { id: string } | null; quantity?: number }>;
            }
        >;
        const availableAtSource = itemTypes.filter(it => {
            const locations = filterValidItemLocations(it.itemLocations ?? []);
            return locations.some(loc => {
                const qty = loc.quantity ?? 0;
                if (qty <= 0) return false;
                if (loc.event?.id && sourceEventIds.has(loc.event.id)) return true;
                if (loc.storageLocation?.id && sourceStorageLocationIds.has(loc.storageLocation.id)) return true;
                return false;
            });
        });
        const existingInFlows = new Set(supplyFlowData ? Object.keys(supplyFlowData.edgeFlowsByItemType) : []);
        const fromFlows = itemTypes.filter(it => existingInFlows.has(it.id));
        const combined = new Map<string, ItemType>();
        for (const it of availableAtSource) combined.set(it.id, it);
        for (const it of fromFlows) if (!combined.has(it.id)) combined.set(it.id, it);
        const withCategory = Array.from(combined.values()) as Array<ItemType & { category?: { name?: string } | null }>;
        return withCategory.sort((a, b) => {
            const catA = a.category?.name ?? '';
            const catB = b.category?.name ?? '';
            const cmp = catA.localeCompare(catB);
            if (cmp !== 0) return cmp;
            return (a.name ?? a.id).localeCompare(b.name ?? b.id);
        });
    }, [itemTypesData?.itemTypes, sourceEventIds, sourceStorageLocationIds, supplyFlowData]);

    /** Item locations at operation sources only (for supply flow cap). */
    const sourceItemLocations = useMemo(() => {
        const locations: Array<{
            itemType: { id: string };
            quantity: number;
            event?: { id: string } | null;
            storageLocation?: { id: string } | null;
        }> = [];
        const itemTypes = (itemTypesData?.itemTypes ?? []) as Array<{
            id: string;
            itemLocations?: Array<{
                event?: { id: string } | null;
                storageLocation?: { id: string } | null;
                quantity?: number;
            }>;
        }>;
        for (const itemType of itemTypes) {
            const itemLocations = filterValidItemLocations(itemType.itemLocations ?? []);
            for (const loc of itemLocations) {
                const qty = loc.quantity ?? 0;
                if (qty <= 0) continue;
                const atSource =
                    (loc.event?.id && sourceEventIds.has(loc.event.id)) ||
                    (loc.storageLocation?.id && sourceStorageLocationIds.has(loc.storageLocation.id));
                if (atSource) {
                    locations.push({
                        itemType: { id: itemType.id },
                        quantity: qty,
                        event: loc.event ?? null,
                        storageLocation: loc.storageLocation ?? null,
                    });
                }
            }
        }
        return locations;
    }, [itemTypesData?.itemTypes, sourceEventIds, sourceStorageLocationIds]);

    /** Packing list requirements by target event for supply flow "Needs" */
    const packingListsByEvent = useMemo(() => {
        const map = new Map<string, Array<{ itemTypeId: string; quantity: number }>>();
        const targetEvents = operation?.targetEvents ?? [];
        for (const ev of targetEvents) {
            const items = (ev.packingListItems ?? []).filter((i: { itemType?: { id: string } | null }) => i.itemType?.id) as Array<{
                itemType: { id: string };
                quantity?: number;
            }>;
            map.set(
                (ev as { id: string }).id,
                items.map(i => ({ itemTypeId: i.itemType.id, quantity: i.quantity ?? 0 }))
            );
        }
        return map;
    }, [operation?.targetEvents]);

    /** Available quantity at a source node (for supply flow cap: prevent exceeding current inventory). */
    const getAvailableAtSource = useCallback(
        (itemTypeId: string, fromNodeIndex: number) => {
            if (!supplyFlowData) return 0;
            const node = supplyFlowData.dagNodes[fromNodeIndex];
            if (!node) return 0;
            if (node.kind === 'source' && node.storageLocationId) {
                return sourceItemLocations
                    .filter(loc => loc.itemType.id === itemTypeId && (loc.storageLocation?.id ?? null) === node.storageLocationId)
                    .reduce((sum, loc) => sum + loc.quantity, 0);
            }
            if (node.kind === 'event' && node.eventId && sourceEventIds.has(node.eventId)) {
                return sourceItemLocations
                    .filter(loc => loc.itemType.id === itemTypeId && (loc.event?.id ?? null) === node.eventId)
                    .reduce((sum, loc) => sum + loc.quantity, 0);
            }
            return 0;
        },
        [supplyFlowData, sourceItemLocations, sourceEventIds]
    );

    const getItemTypeName = useCallback(
        (itemTypeId: string) => {
            const it = (itemTypesData?.itemTypes ?? []).find((t: { id: string }) => t.id === itemTypeId);
            return (it as { name?: string })?.name ?? itemTypeId;
        },
        [itemTypesData?.itemTypes]
    );

    const getItemTypeCategory = useCallback(
        (itemTypeId: string) =>
            (itemTypesData?.itemTypes?.find((t: { id: string }) => t.id === itemTypeId) as { category?: { name?: string } } | undefined)
                ?.category?.name ?? '',
        [itemTypesData?.itemTypes]
    );

    /** Number of source nodes (source storage + source events) for quantity plans */
    const numSourceNodes = useMemo(
        () => (operation?.sourceStorageLocations?.length ?? 0) + (operation?.sourceEvents?.length ?? 0),
        [operation?.sourceStorageLocations, operation?.sourceEvents]
    );

    /** Quantity plans from this operation's tasks (for Inventory tab) */
    const operationQuantityPlans = useMemo(() => {
        if (!supplyFlowData || numSourceNodes === 0 || Object.keys(supplyFlowData.edgeFlowsByItemType).length === 0) return null;
        return quantityPlansFromOperationFlows(supplyFlowData.edgeFlowsByItemType, numSourceNodes, supplyFlowData.getNodeLabel);
    }, [supplyFlowData, numSourceNodes]);

    /** Total available at operation sources (all source locations combined) for an item type */
    const getAvailableAtSourcesTotal = useCallback(
        (itemTypeId: string): number =>
            sourceItemLocations.filter(loc => loc.itemType.id === itemTypeId).reduce((sum, loc) => sum + loc.quantity, 0),
        [sourceItemLocations]
    );

    /** Item types with insufficient inventory at operation sources for this operation's tasks */
    const insufficientOperationItems = useMemo(() => {
        if (!operationQuantityPlans || operationQuantityPlans.size === 0) return [];
        const plansWithTotal = new Map(Array.from(operationQuantityPlans.entries()).map(([id, p]) => [id, { totalNeeded: p.totalNeeded }]));
        return computeInsufficientItemsFromPlans(plansWithTotal, getAvailableAtSourcesTotal);
    }, [operationQuantityPlans, getAvailableAtSourcesTotal]);

    const handleSupplyFlowChange = useCallback(
        async (itemTypeId: string, fromNodeIndex: number, toNodeIndex: number, value: number) => {
            if (!supplyFlowData || !operation) return;
            const key = `${itemTypeId}\t${fromNodeIndex}-${toNodeIndex}`;
            const taskId = supplyFlowData.taskIdByEdge.get(key);
            if (taskId) {
                if (value === 0) {
                    await db.transact([db.tx.packingTasks[taskId].delete()]);
                } else {
                    await db.transact([db.tx.packingTasks[taskId].update({ quantity: value })]);
                }
                return;
            }
            if (value <= 0) return;
            const locs = edgeToTaskLocationIds(supplyFlowData.dagNodes, fromNodeIndex, toNodeIndex);
            const newTaskId = id();
            const txs: Parameters<typeof db.transact>[0] = [
                db.tx.packingTasks[newTaskId].update({
                    quantity: value,
                    completed: false,
                    completedAt: null,
                    ambiguous: false,
                }),
                db.tx.packingTasks[newTaskId].link({ packingOperation: operation.id }),
                db.tx.packingTasks[newTaskId].link({ itemType: itemTypeId }),
            ];
            if (locs.fromEventId) txs.push(db.tx.packingTasks[newTaskId].link({ fromEvent: locs.fromEventId }));
            if (locs.fromStorageLocationId)
                txs.push(db.tx.packingTasks[newTaskId].link({ fromStorageLocation: locs.fromStorageLocationId }));
            if (locs.toEventId) txs.push(db.tx.packingTasks[newTaskId].link({ toEvent: locs.toEventId }));
            if (locs.toStorageLocationId) txs.push(db.tx.packingTasks[newTaskId].link({ toStorageLocation: locs.toStorageLocationId }));
            await db.transact(txs);
        },
        [supplyFlowData, operation]
    );

    const completedCount = useMemo(() => {
        return tasks.filter(t => t.completed).length;
    }, [tasks]);

    const totalCount = tasks.length;

    const linkedTrip = (operation as { trip?: { id?: string; name?: string } | null })?.trip ?? null;
    const handleBack = () => {
        navigate(linkedTrip?.id ? `/logistics/trip/${linkedTrip.id}` : '/logistics/operations');
    };
    const backButtonLabel = linkedTrip?.id ? 'Back to trip' : 'Back to Logistics';

    const basePath = operationId ? `/logistics/operation/${operationId}` : '';
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const path =
            newValue === 1
                ? `${basePath}/confirmation`
                : newValue === 2
                  ? `${basePath}/supply`
                  : newValue === 3
                    ? `${basePath}/inventory`
                    : `${basePath}/tasks`;
        navigate(path);
    };

    const handleCompleteOperation = async () => {
        if (!operation || isCompleting) return;

        setIsCompleting(true);
        try {
            const now = new Date();
            await db.transact([
                db.tx.packingOperations[operation.id].update({
                    status: 'completed',
                    completedAt: now,
                }),
            ]);

            setSuccessMessage('Operation completed successfully');
            setCompleteDialogOpen(false);
            const linkedTripId = (operation as { trip?: { id?: string } | null }).trip?.id;
            setTimeout(() => {
                navigate(linkedTripId ? `/logistics/trip/${linkedTripId}` : '/logistics/operations');
            }, 1500);
        } catch (error: any) {
            console.error('Failed to complete operation:', error);
            setSuccessMessage('Failed to complete operation. Please try again.');
        } finally {
            setIsCompleting(false);
        }
    };

    if (isLoading) {
        return (
            <DefaultLayout>
                <Box display="flex" justifyContent="center" padding={4}>
                    <CircularProgress />
                </Box>
            </DefaultLayout>
        );
    }

    if (error) {
        return (
            <DefaultLayout>
                <Alert severity="error">Error loading operation: {error.message}</Alert>
            </DefaultLayout>
        );
    }

    if (!operation) {
        return (
            <DefaultLayout>
                <Alert severity="error">Operation not found</Alert>
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <GlobalHeader title="Packing Operation" />
                <Container maxWidth="lg" disableGutters sx={{ flexGrow: 1, py: 3, px: { xs: 2, sm: 3 }, minWidth: 0, maxWidth: '100%' }}>
                    {/* Back Button */}
                    <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
                        {backButtonLabel}
                    </Button>

                    {/* Operation Header */}
                    <OperationHeader operation={operation} completedCount={completedCount} totalCount={totalCount} />

                    {/* Completed Operation Info Banner */}
                    {operation.status === 'completed' && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            This operation is completed and cannot be modified. It has been moved to history.
                        </Alert>
                    )}

                    {/* Tab Navigation */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, minWidth: 0 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="operation tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        Tasks
                                        {totalCount > 0 && (
                                            <Chip label={totalCount} size="small" sx={{ height: 20, fontSize: '0.75rem', minWidth: 20 }} />
                                        )}
                                    </Box>
                                }
                                id="operation-tab-0"
                                aria-controls="operation-tabpanel-0"
                            />
                            <Tab label="Final Confirmation" id="operation-tab-1" aria-controls="operation-tabpanel-1" />
                            <Tab label="Supply flow" id="operation-tab-2" aria-controls="operation-tabpanel-2" />
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        Inventory
                                        {insufficientOperationItems.length > 0 && (
                                            <Chip
                                                icon={<WarningIcon />}
                                                label={`${insufficientOperationItems.length} insufficient`}
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                }
                                id="operation-tab-3"
                                aria-controls="operation-tabpanel-3"
                            />
                        </Tabs>
                    </Box>

                    {/* Tab Content */}
                    <Box sx={{ position: 'relative' }}>
                        {/* Current Inventory Panel */}
                        <CurrentInventoryPanel
                            operation={operation}
                            open={inventoryPanelOpen}
                            onClose={() => setInventoryPanelOpen(false)}
                        />

                        {/* Tasks Tab */}
                        <TabPanel value={activeTab} index={0} idPrefix="operation">
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2" component="span">
                                    Task checkboxes <strong>record item moves</strong>. Checking a task updates inventory (items are marked
                                    as moved). Use this tab to track what has actually been packed or unpacked.
                                </Typography>
                            </Alert>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <PackingTasksList
                                        operation={operation}
                                        tasks={tasks}
                                        allItemLocations={allItemLocations}
                                        onToggleInventoryPanel={() => setInventoryPanelOpen(!inventoryPanelOpen)}
                                    />
                                </Box>
                            </Box>
                        </TabPanel>

                        {/* Final Confirmation Tab */}
                        <TabPanel value={activeTab} index={1} idPrefix="operation">
                            <FinalConfirmationTab operation={operation} />
                        </TabPanel>

                        {/* Supply flow Tab */}
                        <TabPanel value={activeTab} index={2} idPrefix="operation">
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2" component="span">
                                    You can change the quantity values below to update the operation&apos;s task quantities. Edits are saved
                                    immediately and will appear on the Tasks tab.
                                </Typography>
                            </Alert>
                            {linkedTripId && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2" component="span">
                                        Trailer–cargo pairing rules are checked against the linked trip. Conflicts show as warnings on each
                                        leg (orange outline) and do not block saving.
                                    </Typography>
                                </Alert>
                            )}
                            {supplyFlowData && supplyFlowData.dagNodes.length > 0 && supplyFlowData.dagEdges.length > 0 ? (
                                <SupplyFlowSection
                                    dagNodes={supplyFlowData.dagNodes}
                                    dagEdges={supplyFlowData.dagEdges}
                                    routeStructure={tripTrailerCtx?.routeStructure ?? null}
                                    edgeFlowsByItemType={supplyFlowData.edgeFlowsByItemType}
                                    packingListsByEvent={packingListsByEvent}
                                    planItemTypes={planItemTypes}
                                    getItemTypeName={getItemTypeName}
                                    getNodeLabel={supplyFlowData.getNodeLabel}
                                    editMode={operation.status === 'active'}
                                    onEdgeFlowChange={handleSupplyFlowChange}
                                    enableConservationChecks={false}
                                    getAvailableAtSource={getAvailableAtSource}
                                    trailersPerEdge={linkedTripId ? (tripTrailerCtx?.trailersPerEdge ?? null) : null}
                                    incompatibilityKeys={linkedTripId ? tripTrailerCtx?.incompatibilityKeys : undefined}
                                    resolveTrailerBanIndices={linkedTripId ? resolveTrailerBanIndices : undefined}
                                    trailerBanSeverity="warning"
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No supply flow for this operation. Add tasks (on the Tasks tab) to see and edit quantities by leg.
                                </Typography>
                            )}
                        </TabPanel>

                        {/* Inventory Tab */}
                        <TabPanel value={activeTab} index={3} idPrefix="operation">
                            {linkedTrip?.id && linkedTrip?.name && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Part of trip:{' '}
                                    <Link to={`/logistics/trip/${linkedTrip.id}/inventory`} style={{ fontWeight: 600 }}>
                                        {linkedTrip.name}
                                    </Link>
                                </Alert>
                            )}
                            {operationQuantityPlans != null && operationQuantityPlans.size > 0 ? (
                                <>
                                    {insufficientOperationItems.length > 0 && (
                                        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                                            {insufficientOperationItems.length}{' '}
                                            {insufficientOperationItems.length === 1 ? 'item has' : 'items have'} insufficient inventory at
                                            this operation&apos;s sources
                                        </Alert>
                                    )}
                                    <QuantitySummary
                                        quantityPlans={operationQuantityPlans}
                                        getAvailable={getAvailableAtSourcesTotal}
                                        getItemTypeName={getItemTypeName}
                                        getItemTypeCategory={getItemTypeCategory}
                                        getTrailerName={id => (supplyFlowData ? supplyFlowData.getNodeLabel(Number(id)) : id)}
                                    />
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No tasks for this operation yet. Add tasks (or create them from the Supply flow tab) to see inventory
                                    needs vs available at sources.
                                </Typography>
                            )}
                        </TabPanel>
                    </Box>

                    {/* Complete button (below tabs, visible on all tabs) */}
                    {operation.status === 'active' && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => setCompleteDialogOpen(true)}
                                sx={{ minHeight: 48, width: { xs: '100%', sm: 'auto' } }}
                            >
                                Complete operation
                            </Button>
                        </Box>
                    )}
                </Container>
            </Box>

            {/* Complete Operation Dialog */}
            {operation && (
                <CompleteOperationDialog
                    open={completeDialogOpen}
                    onClose={() => setCompleteDialogOpen(false)}
                    onConfirm={handleCompleteOperation}
                    operation={operation}
                />
            )}

            {/* Success Snackbar */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={() => setSuccessMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSuccessMessage(null)}
                    severity={successMessage?.includes('Failed') ? 'error' : 'success'}
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </DefaultLayout>
    );
}
